import {
    MollieComponentTypeKeys,
    MollieEventPayload
} from "@omni/types/packages/mollie.types";
import { cn } from "@omni/utils/styles";
import { Path, PathValue, useFormContext } from "react-hook-form";

import {
    BaseField,
    BaseFieldProps
} from "@/components/Form/RHF/FormFields/BaseField/BaseField.component";
import { FormFieldProps } from "@/components/Form/RHF/FormFields/formField.types";

import { SecureFieldFormValues } from "./secureField.schema";
import { SecureInputField } from "./secureInputField.component";

export type SecureFieldProps<T extends SecureFieldFormValues> = FormFieldProps<
    Omit<
        BaseFieldProps<SecureFieldFormValues, MollieComponentTypeKeys>,
        "required"
    >,
    T
>;

const SecureField = <T extends SecureFieldFormValues>({
    name,
    label,
    tooltip
}: SecureFieldProps<T>) => {
    const { setError, clearErrors, setValue } =
        useFormContext<SecureFieldFormValues>();

    const onUpdate = (e: MollieEventPayload) => {
        if (!e.error) {
            clearErrors(name);
        } else {
            setError(name, {
                message: () => () => e.error
            });
        }

        if (e.touched || e.dirty) {
            setValue(name, "" as PathValue<T, Path<T>>, {
                shouldDirty: e.dirty,
                shouldTouch: e.touched,
                shouldValidate: false
            });
        }
    };

    return (
        <BaseField<SecureFieldFormValues, Path<SecureFieldFormValues>>
            label={label}
            name={name}
            required={true}
            tooltip={tooltip}
        >
            {({ fieldState }) => {
                return (
                    <SecureInputField
                        name={name}
                        className={cn("tw:py-0")}
                        isInvalid={fieldState.isInvalid}
                        isValid={fieldState.isValid}
                        onChange={onUpdate}
                        onBlur={onUpdate}
                    />
                );
            }}
        </BaseField>
    );
};

export { SecureField };
